import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { LayoutDashboard, Plus, CheckCircle, Rocket, Play, AlertCircle } from 'lucide-react'
import { getPlates, createPlate, verifyPlate, deployPlate } from '../api/brainer'
import { SearchInput } from '../components/SearchInput'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import type { BPlate, BPlateStatus } from '../types'

const STATUS_BADGE: Record<BPlateStatus, { label: string; cls: string }> = {
  draft:    { label: 'Draft',    cls: 'bg-paper-sunken text-ink-dim' },
  verified: { label: 'Verified', cls: 'bg-blue-50 text-blue-600' },
  deployed: { label: 'Deployed', cls: 'bg-emerald-50 text-emerald-600' },
  error:    { label: 'Error',    cls: 'bg-red-50 text-red-500' },
}

export function BrainerPlates() {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data: plates = [], isLoading } = useQuery({
    queryKey: ['b-plates'],
    queryFn: () => getPlates().then(r => r.data),
  })

  const [showCreate, setShowCreate]   = useState(false)
  const [form, setForm] = useState({ name: '', description: '', subjectType: '', width: '' })

  const openCreate = () => { setForm({ name: '', description: '', subjectType: '', width: '' }); setShowCreate(true) }

  const create = useMutation({
    mutationFn: () => createPlate({ name: form.name, description: form.description || undefined, subjectType: form.subjectType || undefined, width: form.width || undefined }),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ['b-plates'] }); setShowCreate(false); navigate(`/brainer/plates/${res.data.id}`) },
  })

  const verify = useMutation({
    mutationFn: (id: string) => verifyPlate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-plates'] }),
  })

  const deploy = useMutation({
    mutationFn: (id: string) => deployPlate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['b-plates'] }),
  })

  const formValid = form.name.trim().length > 0

  const [search, setSearch] = useState('')
  const filtered = (plates as BPlate[]).filter(p =>
    [p.name, p.description ?? '', p.subjectType ?? ''].some(v => v.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Plates</h1>
          <p className="text-sm text-ink-dim mt-0.5">Define views that run Drinkers and display structured data</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search plates" />
          <Button icon={Plus} onClick={openCreate}>New plate</Button>
        </div>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
      ) : plates.length === 0 ? (
        <EmptyState icon={LayoutDashboard} title="No plates" description="Create a plate to build data views powered by Drinkers" />
      ) : filtered.length === 0 ? (
        <EmptyState icon={LayoutDashboard} title="No matching plates" description="Try a different search" />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Subject type</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const badge = STATUS_BADGE[p.status]
                return (
                  <tr key={p.id}>
                    <td>
                      <button onClick={() => navigate(`/brainer/plates/${p.id}`)} className="font-medium text-accent hover:underline text-left">
                        {p.name}
                      </button>
                      {p.description && <p className="text-xs text-ink-dim mt-0.5 truncate max-w-xs">{p.description}</p>}
                    </td>
                    <td className="text-ink-dim">{p.subjectType || '—'}</td>
                    <td>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${badge.cls}`}>
                        {p.status === 'error' && <AlertCircle size={10} />}
                        {badge.label}
                      </span>
                      {p.status === 'error' && p.errorMessage && (
                        <p className="text-theme-xs text-red-500 mt-0.5 max-w-xs truncate">{p.errorMessage.split('\n')[0]}</p>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-3 justify-end">
                        {(p.status === 'draft' || p.status === 'error') && (
                          <button onClick={() => verify.mutate(p.id)} title="Verify" className="text-ink-dim hover:text-accent transition-colors">
                            <CheckCircle size={13} />
                          </button>
                        )}
                        {p.status === 'verified' && (
                          <button onClick={() => deploy.mutate(p.id)} title="Deploy" className="text-ink-dim hover:text-emerald-600 transition-colors">
                            <Rocket size={13} />
                          </button>
                        )}
                        {p.status === 'deployed' && (
                          <button onClick={() => navigate(`/brainer/plates/${p.id}/run`)} title="Run" className="text-ink-dim hover:text-emerald-600 transition-colors">
                            <Play size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <Modal
          title="New plate"
          onClose={() => setShowCreate(false)}
          size="sm"
        >
          <PlateForm form={form} onChange={setForm} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={() => create.mutate()}
              disabled={!formValid || create.isPending}
              loading={create.isPending}
            >
              Create
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

export const WIDTH_OPTIONS = [
  { value: '_default',  label: 'Full width (default)' },
  { value: 'max-w-sm',  label: 'Small (max-w-sm · 384px)' },
  { value: 'max-w-md',  label: 'Medium (max-w-md · 448px)' },
  { value: 'max-w-lg',  label: 'Large (max-w-lg · 512px)' },
  { value: 'max-w-xl',  label: 'XL (max-w-xl · 576px)' },
  { value: 'max-w-2xl', label: '2XL (max-w-2xl · 672px)' },
  { value: 'max-w-3xl', label: '3XL (max-w-3xl · 768px)' },
  { value: 'max-w-4xl', label: '4XL (max-w-4xl · 896px)' },
  { value: 'max-w-5xl', label: '5XL (max-w-5xl · 1024px)' },
  { value: 'max-w-6xl', label: '6XL (max-w-6xl · 1152px)' },
  { value: 'max-w-7xl', label: '7XL (max-w-7xl · 1280px)' },
]

function PlateForm({
  form,
  onChange,
}: {
  form: { name: string; description: string; subjectType: string; width: string }
  onChange: (f: { name: string; description: string; subjectType: string; width: string }) => void
}) {
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    onChange({ ...form, [k]: e.target.value })

  return (
    <div className="space-y-4">
      <Field label="Name *">
        <input value={form.name} onChange={set('name')} placeholder="e.g. Company overview" className="field" />
      </Field>
      <Field label="Description">
        <textarea value={form.description} onChange={set('description')} rows={2} className="field resize-none" />
      </Field>
      <Field label="Subject type">
        <input value={form.subjectType} onChange={set('subjectType')} placeholder="e.g. Company, Order (optional)" className="field" />
        <p className="text-xs text-ink-dim mt-1">Links this plate to a specific entity type. Used to pass a subject ID at run time.</p>
      </Field>
      <Field label="Container width">
        <Select
          value={form.width || '_default'}
          onValueChange={(v) => onChange({ ...form, width: v === '_default' ? '' : v })}
        >
          {WIDTH_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </Select>
      </Field>
    </div>
  )
}
